import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface UrgentFollowup {
  id: number;
  case_id: number;
  text: string;
  due_at: string | null;
  created_at: string;
  client_name: string | null;
  file_reference: string | null;
  file_status: string | null;
}

function daysUntil(dueAt: string | null): number | null {
  if (!dueAt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueAt);
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

function dueLabel(dueAt: string | null): string {
  const d = daysUntil(dueAt);
  if (d === null) return "No deadline";
  if (d < 0) return `${Math.abs(d)}d overdue`;
  if (d === 0) return "Today";
  if (d === 1) return "Tomorrow";
  return `In ${d}d`;
}

function dueColor(dueAt: string | null): string {
  const d = daysUntil(dueAt);
  if (d === null) return "var(--text-muted)";
  if (d < 0) return "var(--danger)";
  if (d <= 3) return "var(--warning)";
  return "var(--text-secondary)";
}

export default function UrgentFollowupsPanel({ onOpenCase }: { onOpenCase?: (caseId: number) => void }) {
  const [items, setItems] = useState<UrgentFollowup[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch("/api/followups/urgent");
      if (r.ok) setItems(await r.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const urgentCount = items.filter(f => {
    const d = daysUntil(f.due_at);
    return d !== null && d <= 3;
  }).length;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div
        className="card-header"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setCollapsed(c => !c)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h2 className="card-title" style={{ margin: 0 }}>Urgent Follow-ups</h2>
          {urgentCount > 0 && (
            <span className="badge badge-red" style={{ fontSize: 10 }}>{urgentCount}</span>
          )}
        </div>
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{collapsed ? "▸" : "▾"}</span>
      </div>
      {!collapsed && (
        <div style={{ padding: "8px 12px 12px", maxHeight: 340, overflowY: "auto" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 16, color: "var(--text-muted)", fontSize: 13 }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)", fontSize: 13 }}>
              Nothing urgent. 🎉
            </div>
          ) : (
            items.map(f => (
              <div
                key={f.id}
                onClick={() => onOpenCase?.(f.case_id)}
                style={{
                  borderLeft: `3px solid ${dueColor(f.due_at)}`,
                  padding: "6px 10px",
                  marginBottom: 8,
                  background: "var(--bg-light)",
                  borderRadius: 6,
                  cursor: onOpenCase ? "pointer" : "default",
                }}
              >
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word", marginBottom: 4 }}>
                  {f.text}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: dueColor(f.due_at) }}>{dueLabel(f.due_at)}</span>
                  <span>•</span>
                  <span>{f.client_name || "—"}</span>
                  {f.file_reference && (<><span>•</span><span>{f.file_reference}</span></>)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
